import { API_URL } from "../../shared/utils/Util";
import { fetchT, getFetchT } from "../../shared/utils/TypedUtil";
import { PointsConfig } from "../points-config";
import {
  ComboAddonSchema,
  ComboAddonSchemaWithUsage,
  ComboSchema,
  CreateComboAddonSchemaResponse,
  CreateComboSchemaResponse,
  GetComboAddonSchemasResponse,
  GetComboSchemasResponse,
  ItemSchemaComboUsage,
} from "./types";

// Client for the combo admin API (backend services/api/rest/combo_schemas.go).
// Errors are passed through untouched so callers can surface the backend's
// validation message with useNotification().notifyError.

type OnError = (err: unknown) => void;

const base = (restaurantId: string) => `${API_URL}/v1/r/${restaurantId}`;

const menuTypeQuery = (menuType?: string) =>
  menuType ? `?menu_type=${encodeURIComponent(menuType)}` : "";

// --- Combos ---

export function getComboSchemas(
  restaurantId: string,
  menuType: string | undefined,
  onSuccess: (schemas: ComboSchema[]) => void,
  onError: OnError
) {
  getFetchT<GetComboSchemasResponse>(
    `${base(restaurantId)}/combo_schemas${menuTypeQuery(menuType)}`,
    (result) => onSuccess(result.schemas ?? []),
    onError
  );
}

export function createComboSchema(
  restaurantId: string,
  combo: ComboSchema,
  onSuccess: (id: string) => void,
  onError: OnError
) {
  fetchT<CreateComboSchemaResponse>(
    "POST",
    `${base(restaurantId)}/combo_schemas`,
    combo,
    (result) => onSuccess(result.id),
    onError
  );
}

export function updateComboSchema(
  restaurantId: string,
  id: string,
  combo: ComboSchema,
  onSuccess: () => void,
  onError: OnError
) {
  fetchT<{}>("PUT", `${base(restaurantId)}/combo_schemas/${id}`, combo, onSuccess, onError);
}

export function deleteComboSchema(
  restaurantId: string,
  id: string,
  onSuccess: () => void,
  onError: OnError
) {
  fetchT<{}>("DELETE", `${base(restaurantId)}/combo_schemas/${id}`, {}, onSuccess, onError);
}

// --- Add-ons ---

export function getComboAddonSchemas(
  restaurantId: string,
  menuType: string | undefined,
  onSuccess: (schemas: ComboAddonSchemaWithUsage[]) => void,
  onError: OnError
) {
  getFetchT<GetComboAddonSchemasResponse>(
    `${base(restaurantId)}/combo_addon_schemas${menuTypeQuery(menuType)}`,
    (result) =>
      onSuccess(
        (result.schemas ?? []).map((a) => ({
          ...a,
          tiers: a.tiers ?? [],
          modification_prices: a.modification_prices ?? [],
          used_by: a.used_by ?? [],
        }))
      ),
    onError
  );
}

// Strips the response-only `used_by` before sending an add-on back.
const addonBody = (addon: ComboAddonSchema): ComboAddonSchema => {
  const { used_by: _usedBy, ...rest } = addon as ComboAddonSchemaWithUsage;
  return rest;
};

export function createComboAddonSchema(
  restaurantId: string,
  addon: ComboAddonSchema,
  onSuccess: (id: string) => void,
  onError: OnError
) {
  fetchT<CreateComboAddonSchemaResponse>(
    "POST",
    `${base(restaurantId)}/combo_addon_schemas`,
    addonBody(addon),
    (result) => onSuccess(result.id),
    onError
  );
}

export function updateComboAddonSchema(
  restaurantId: string,
  id: string,
  addon: ComboAddonSchema,
  onSuccess: () => void,
  onError: OnError
) {
  fetchT<{}>(
    "PUT",
    `${base(restaurantId)}/combo_addon_schemas/${id}`,
    addonBody(addon),
    onSuccess,
    onError
  );
}

export function deleteComboAddonSchema(
  restaurantId: string,
  id: string,
  onSuccess: () => void,
  onError: OnError
) {
  fetchT<{}>("DELETE", `${base(restaurantId)}/combo_addon_schemas/${id}`, {}, onSuccess, onError);
}

// --- Item usage ---

export function getItemSchemaComboUsage(
  restaurantId: string,
  itemSchemaId: string,
  onSuccess: (usage: ItemSchemaComboUsage) => void,
  onError: OnError
) {
  getFetchT<ItemSchemaComboUsage>(
    `${base(restaurantId)}/item_schemas/${itemSchemaId}/usage`,
    (result) => onSuccess({ entrees: result.entrees ?? [], addon_tiers: result.addon_tiers ?? [] }),
    onError
  );
}

// --- Points config (for the tier points placeholder and combo preview) ---

export function getPointsConfig(
  restaurantId: string,
  onSuccess: (config: PointsConfig | undefined) => void
) {
  getFetchT<PointsConfig>(`${base(restaurantId)}/points_config`, onSuccess, () =>
    onSuccess(undefined)
  );
}
